import React from 'react';
import { Phone, BookOpen, AlertTriangle, CheckCircle, Package, Shield } from 'lucide-react';
export function CargoSecurementRulesPage() {
  return <div className="min-h-screen bg-gray-50" data-id="element-4679">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#B1050B] to-[#8A0408] text-white py-16" data-id="element-4680">
        <div className="container mx-auto px-4" data-id="element-4681">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-center" data-id="element-4682">
            FMCSA Cargo Securement Rules
          </h1>
          <p className="text-xl text-gray-100 text-center" data-id="element-4683">
            Page updated: March 22, 2024
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12" data-id="element-4684">
        <div className="max-w-4xl mx-auto" data-id="element-4685">
          {/* Introduction */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-4686">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-4687">
              <BookOpen className="mr-3 text-[#B1050B]" size={28} data-id="element-4688" />
              Overview
            </h2>

            <p className="text-lg text-gray-700 mb-4" data-id="element-4689">
              The Federal Motor Carrier Safety Administration (FMCSA)
              implemented new cargo securement rules on{' '}
              <strong data-id="element-4690">January 1, 2004</strong>, derived from the North American
              Cargo Securement Standard Model Regulations.
            </p>

            <p className="text-gray-700 mb-4" data-id="element-4691">
              These changes arose from extensive research and consultations
              across the U.S. and Canada, aiming to harmonize cargo securement
              regulations across North America, including Mexico, as much as
              possible.
            </p>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-6" data-id="element-4692">
              <h3 className="font-bold text-gray-900 mb-2" data-id="element-4693">
                Primary Objective
              </h3>
              <p className="text-gray-700" data-id="element-4694">
                Enhance road safety by minimizing accidents caused by cargo
                shifting or falling from commercial vehicles in interstate
                commerce.
              </p>
            </div>
          </div>

          {/* Applicability */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-4695">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-4696">
              Applicability of the New Rules
            </h2>

            <p className="text-gray-700 mb-4" data-id="element-4697">
              The new cargo securement rules apply to the same types of vehicles
              and cargo as the old rules, covering all cargo-carrying commercial
              motor vehicles (as defined in 49 CFR 390.5) operated in interstate
              commerce.
            </p>

            <div className="bg-green-50 rounded-lg p-6 mb-4" data-id="element-4698">
              <h3 className="font-bold text-gray-900 mb-3" data-id="element-4699">✅ Applies To:</h3>
              <p className="text-gray-700" data-id="element-4700">All types of articles of cargo</p>
            </div>

            <div className="bg-red-50 rounded-lg p-6" data-id="element-4701">
              <h3 className="font-bold text-gray-900 mb-3" data-id="element-4702">❌ Exceptions:</h3>
              <p className="text-gray-700" data-id="element-4703">
                Commodities in bulk that lack structure or fixed shape (e.g.,
                liquids, gases, grain, liquid concrete, sand, gravel,
                aggregates) transported in a tank, hopper, box or similar device
                that forms part of the structure of a commercial motor vehicle.
              </p>
            </div>
          </div>

          {/* Performance Criteria */}
          <div className="bg-gradient-to-r from-[#B1050B] to-[#8A0408] text-white rounded-lg shadow-xl p-8 mb-8" data-id="element-4704">
            <h2 className="text-3xl font-bold mb-6 flex items-center" data-id="element-4705">
              <Shield className="mr-3" size={32} data-id="element-4706" />
              Performance Criteria
            </h2>

            <p className="text-lg mb-6" data-id="element-4707">
              FMCSA requires that cargo securement systems be capable of
              withstanding the forces associated with the following three
              deceleration/accelerations, applied separately:
            </p>

            <div className="space-y-4" data-id="element-4708">
              <div className="bg-white/10 rounded-lg p-6" data-id="element-4709">
                <p className="text-2xl font-bold mb-2" data-id="element-4710">0.8 g</p>
                <p className="text-gray-200" data-id="element-4711">
                  Deceleration in the forward direction
                </p>
              </div>

              <div className="bg-white/10 rounded-lg p-6" data-id="element-4712">
                <p className="text-2xl font-bold mb-2" data-id="element-4713">0.5 g</p>
                <p className="text-gray-200" data-id="element-4714">
                  Acceleration in the rearward direction
                </p>
              </div>

              <div className="bg-white/10 rounded-lg p-6" data-id="element-4715">
                <p className="text-2xl font-bold mb-2" data-id="element-4716">0.5 g</p>
                <p className="text-gray-200" data-id="element-4717">
                  Acceleration in a lateral direction
                </p>
              </div>
            </div>

            <div className="bg-yellow-50 text-gray-900 rounded-lg p-6 mt-6" data-id="element-4718">
              <p className="font-semibold" data-id="element-4719">
                Motor carriers are NOT required to conduct testing of cargo
                securement systems. Cargo immobilized or secured in accordance
                with the general or commodity-specific rules are considered to
                meet the performance criteria.
              </p>
            </div>
          </div>

          {/* Key Highlights */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-4720">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-4721">
              Key Highlights of the Regulations
            </h2>

            <div className="space-y-6" data-id="element-4722">
              <div className="border-l-4 border-blue-500 pl-6" data-id="element-4723">
                <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-4724">
                  Securement Devices
                </h3>
                <p className="text-gray-700 mb-3" data-id="element-4725">
                  All devices and systems used for securing cargo must meet
                  performance criteria. FMCSA has updated references to
                  manufacturing standards for various types of tiedowns.
                </p>
                <div className="bg-blue-50 rounded p-4" data-id="element-4726">
                  <p className="text-gray-800" data-id="element-4727">
                    <strong data-id="element-4728">Important:</strong> Existing cargo securement
                    devices compliant with old regulations are deemed
                    satisfactory under new rules. Motor carriers do NOT need to
                    purchase new equipment solely for compliance.
                  </p>
                </div>
              </div>

              <div className="border-l-4 border-green-500 pl-6" data-id="element-4729">
                <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-4730">
                  Proper Use and Placement of Tiedowns
                </h3>
                <ul className="space-y-2 text-gray-700" data-id="element-4731">
                  <li className="flex items-start" data-id="element-4732">
                    <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-4733" />
                    <span data-id="element-4734">Tiedowns must remain secure during transit</span>
                  </li>
                  <li className="flex items-start" data-id="element-4735">
                    <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-4736" />
                    <span data-id="element-4737">
                      Position tiedowns inboard of rub rails when feasible
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-4738">
                    <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-4739" />
                    <span data-id="element-4740">
                      Use edge protection to prevent damage from sharp cargo
                      edges
                    </span>
                  </li>
                </ul>
              </div>

              <div className="border-l-4 border-yellow-500 pl-6" data-id="element-4741">
                <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-4742">
                  Unmarked Tiedowns
                </h3>
                <p className="text-gray-700" data-id="element-4743">
                  No prohibition on using unmarked tiedowns, but certain
                  assumptions about working load limits apply. Unmarked welded
                  steel chain is considered to have working load limit equal to
                  grade 30 proof coil.
                </p>
              </div>
            </div>
          </div>

          {/* General Securement Rules */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-4744">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-4745">
              General Securement and Placement Rules
            </h2>

            <div className="space-y-6" data-id="element-4746">
              <div className="bg-blue-50 rounded-lg p-6" data-id="element-4747">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-4748">General Rule</h3>
                <p className="text-gray-700" data-id="element-4749">
                  Cargo must be firmly immobilized or secured on or within a
                  vehicle by structures of adequate strength, dunnage, dunnage
                  bags, shoring bars, tiedowns or a combination of these.
                </p>
              </div>

              <div className="bg-green-50 rounded-lg p-6" data-id="element-4750">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-4751">
                  Cargo Placement and Restraint
                </h3>
                <ul className="space-y-2 text-gray-700" data-id="element-4752">
                  <li data-id="element-4753">
                    • Articles likely to roll must be restrained by chocks,
                    wedges, cradle or equivalent means
                  </li>
                  <li data-id="element-4754">
                    • Articles placed beside each other must be in direct
                    contact OR prevented from shifting towards each other
                  </li>
                </ul>
              </div>

              <div className="bg-purple-50 rounded-lg p-6" data-id="element-4755">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-4756">
                  Minimum Working Load Limit
                </h3>
                <p className="text-gray-700 mb-2" data-id="element-4757">
                  The aggregate working load limit of any securement system must
                  be <strong data-id="element-4758">at least one-half the weight</strong> of the
                  article or group of articles.
                </p>
              </div>
            </div>
          </div>

          {/* Minimum Number of Tiedowns */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-4759">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-4760">
              Minimum Number of Tiedowns
            </h2>

            <p className="text-gray-700 mb-6" data-id="element-4761">
              When an article of cargo is NOT blocked or positioned to prevent
              movement in the forward direction:
            </p>

            <div className="space-y-4" data-id="element-4762">
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4" data-id="element-4763">
                <p className="font-bold text-gray-900 mb-2" data-id="element-4764">
                  1 Tiedown Required:
                </p>
                <p className="text-gray-700" data-id="element-4765">
                  Articles 5 ft or less in length AND 1,100 lbs or less in
                  weight
                </p>
              </div>

              <div className="bg-green-50 border-l-4 border-green-500 p-4" data-id="element-4766">
                <p className="font-bold text-gray-900 mb-2" data-id="element-4767">
                  2 Tiedowns Required:
                </p>
                <ul className="text-gray-700 space-y-1" data-id="element-4768">
                  <li data-id="element-4769">
                    • 5 ft or less in length and MORE than 1,100 lbs in weight,
                    OR
                  </li>
                  <li data-id="element-4770">
                    • Greater than 5 ft but less than 10 ft (regardless of
                    weight)
                  </li>
                </ul>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4" data-id="element-4771">
                <p className="font-bold text-gray-900 mb-2" data-id="element-4772">
                  Longer than 10 ft:
                </p>
                <p className="text-gray-700" data-id="element-4773">
                  Two tiedowns for first 10 ft + one additional tiedown for
                  every 10 ft (or fraction thereof) beyond first 10 ft
                </p>
              </div>

              <div className="bg-purple-50 border-l-4 border-purple-500 p-4" data-id="element-4774">
                <p className="font-bold text-gray-900 mb-2" data-id="element-4775">
                  If Blocked/Braced:
                </p>
                <p className="text-gray-700" data-id="element-4776">
                  At least one tiedown for every 10 ft of article length (or
                  fraction thereof)
                </p>
              </div>
            </div>
          </div>

          {/* Commodity-Specific Requirements */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-4777">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-4778">
              <Package className="mr-3 text-[#B1050B]" size={28} data-id="element-4779" />
              Commodity-Specific Securement Requirements
            </h2>

            <p className="text-gray-700 mb-6" data-id="element-4780">
              FMCSA has adopted detailed requirements for the securement of the
              following commodities. These commodity-specific guidelines take
              precedence over the general rules when applicable:
            </p>

            <div className="grid md:grid-cols-2 gap-4" data-id="element-4781">
              <div className="bg-blue-50 rounded-lg p-4" data-id="element-4782">
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-4783">
                  §393.116 - Logs
                </h3>
                <p className="text-sm text-gray-700" data-id="element-4784">
                  Transportation of logs with specific exceptions for unitized
                  logs and firewood
                </p>
              </div>

              <div className="bg-green-50 rounded-lg p-4" data-id="element-4785">
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-4786">
                  §393.118 - Dressed Lumber
                </h3>
                <p className="text-sm text-gray-700" data-id="element-4787">
                  Bundles of dressed lumber, packaged lumber, and similar
                  building products
                </p>
              </div>

              <div className="bg-yellow-50 rounded-lg p-4" data-id="element-4788">
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-4789">
                  §393.120 - Metal Coils
                </h3>
                <p className="text-sm text-gray-700" data-id="element-4790">
                  Metal coils weighing 5,000 lbs or more (individually or
                  grouped)
                </p>
              </div>

              <div className="bg-purple-50 rounded-lg p-4" data-id="element-4791">
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-4792">
                  §393.122 - Paper Rolls
                </h3>
                <p className="text-sm text-gray-700" data-id="element-4793">
                  Paper rolls weighing 5,000 lbs or more (individually or
                  together)
                </p>
              </div>

              <div className="bg-red-50 rounded-lg p-4" data-id="element-4794">
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-4795">
                  §393.124 - Concrete Pipe
                </h3>
                <p className="text-sm text-gray-700" data-id="element-4796">
                  Concrete pipe on flatbed and lowboy trailers
                </p>
              </div>

              <div className="bg-indigo-50 rounded-lg p-4" data-id="element-4797">
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-4798">
                  §393.126 - Intermodal Containers
                </h3>
                <p className="text-sm text-gray-700" data-id="element-4799">
                  Freight containers for interchangeable multi-mode
                  transportation
                </p>
              </div>

              <div className="bg-pink-50 rounded-lg p-4" data-id="element-4800">
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-4801">
                  §393.128 - Automobiles
                </h3>
                <p className="text-sm text-gray-700" data-id="element-4802">
                  Automobiles, light trucks, and vans weighing 10,000 lbs or
                  less
                </p>
              </div>

              <div className="bg-orange-50 rounded-lg p-4" data-id="element-4803">
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-4804">
                  §393.130 - Heavy Vehicles
                </h3>
                <p className="text-sm text-gray-700" data-id="element-4805">
                  Heavy vehicles, equipment and machinery weighing 10,000 lbs or
                  more
                </p>
              </div>

              <div className="bg-teal-50 rounded-lg p-4" data-id="element-4806">
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-4807">
                  §393.132 - Flattened Vehicles
                </h3>
                <p className="text-sm text-gray-700" data-id="element-4808">
                  Vehicles that have been intentionally flattened or crushed
                </p>
              </div>

              <div className="bg-cyan-50 rounded-lg p-4" data-id="element-4809">
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-4810">
                  §393.134 - Roll-on/Roll-off
                </h3>
                <p className="text-sm text-gray-700" data-id="element-4811">
                  Roll-on/roll-off or hook-lift containers
                </p>
              </div>

              <div className="bg-lime-50 rounded-lg p-4" data-id="element-4812">
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-4813">
                  §393.136 - Large Boulders
                </h3>
                <p className="text-sm text-gray-700" data-id="element-4814">
                  Natural, irregularly shaped rock exceeding 11,000 lbs or 2
                  cubic meters
                </p>
              </div>
            </div>
          </div>

          {/* Key Takeaways */}
          <div className="bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg shadow-xl p-8 mb-8" data-id="element-4815">
            <h2 className="text-3xl font-bold mb-6" data-id="element-4816">Key Takeaways</h2>

            <div className="space-y-3" data-id="element-4817">
              <div className="flex items-start" data-id="element-4818">
                <CheckCircle className="mr-3 flex-shrink-0 mt-1" size={20} data-id="element-4819" />
                <span data-id="element-4820">
                  Rules effective January 1, 2004, based on North American
                  standards
                </span>
              </div>
              <div className="flex items-start" data-id="element-4821">
                <CheckCircle className="mr-3 flex-shrink-0 mt-1" size={20} data-id="element-4822" />
                <span data-id="element-4823">
                  Performance criteria: 0.8g forward, 0.5g rearward, 0.5g
                  lateral
                </span>
              </div>
              <div className="flex items-start" data-id="element-4824">
                <CheckCircle className="mr-3 flex-shrink-0 mt-1" size={20} data-id="element-4825" />
                <span data-id="element-4826">
                  Existing compliant equipment does NOT need replacement
                </span>
              </div>
              <div className="flex items-start" data-id="element-4827">
                <CheckCircle className="mr-3 flex-shrink-0 mt-1" size={20} data-id="element-4828" />
                <span data-id="element-4829">
                  Minimum working load limit: at least one-half cargo weight
                </span>
              </div>
              <div className="flex items-start" data-id="element-4830">
                <CheckCircle className="mr-3 flex-shrink-0 mt-1" size={20} data-id="element-4831" />
                <span data-id="element-4832">
                  Tiedown requirements based on cargo length and weight
                </span>
              </div>
              <div className="flex items-start" data-id="element-4833">
                <CheckCircle className="mr-3 flex-shrink-0 mt-1" size={20} data-id="element-4834" />
                <span data-id="element-4835">11 commodity-specific securement requirements</span>
              </div>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="bg-yellow-50 border-2 border-yellow-500 rounded-lg p-6 mb-8" data-id="element-4836">
            <div className="flex items-start" data-id="element-4837">
              <AlertTriangle className="text-yellow-600 mr-3 flex-shrink-0 mt-1" size={24} data-id="element-4838" />
              <div data-id="element-4839">
                <p className="text-sm text-gray-800" data-id="element-4840">
                  <strong data-id="element-4841">Disclaimer:</strong> This content is for informational
                  purposes only. The information on this page is not legal
                  advice and does not portend to be the official rules. We do
                  our best to provide accurate information, but rules,
                  regulations and laws change so it's important that you confirm
                  the information for yourself at the official FMCSA website.
                </p>
              </div>
            </div>
          </div>

          {/* Official Resources */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-4842">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-4843">
              Official Resources
            </h2>

            <div className="space-y-3" data-id="element-4844">
              <div className="bg-gray-50 rounded-lg p-4" data-id="element-4845">
                <p className="font-semibold text-gray-900 mb-1" data-id="element-4846">
                  Federal Motor Carrier Safety Administration
                </p>
                <p className="text-gray-700 text-sm" data-id="element-4847">
                  Vehicle and Roadside Operations Division (MC-PSV)
                </p>
                <p className="text-gray-700 text-sm" data-id="element-4848">
                  1200 New Jersey Avenue SE
                </p>
                <p className="text-gray-700 text-sm" data-id="element-4849">Washington, DC 20590</p>
                <a href="https://www.fmcsa.dot.gov" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm" data-id="element-4850">
                  www.fmcsa.dot.gov
                </a>
              </div>

              <div className="bg-gray-50 rounded-lg p-4" data-id="element-4851">
                <p className="font-semibold text-gray-900 mb-1" data-id="element-4852">
                  Publication Information
                </p>
                <p className="text-gray-700 text-sm" data-id="element-4853">
                  Publication No.: MC-P/PSV-04-001
                </p>
                <p className="text-gray-700 text-sm" data-id="element-4854">
                  Last updated: Monday, March 3, 2014
                </p>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-lg shadow-xl p-8 text-center" data-id="element-4855">
            <h2 className="text-3xl font-bold mb-4" data-id="element-4856">
              Need Help with Cargo Securement Compliance?
            </h2>
            <p className="text-xl mb-6" data-id="element-4857">
              Expert guidance on FMCSA cargo securement regulations
            </p>
            <a href="tel:2484788382" className="inline-flex items-center bg-[#B1050B] hover:bg-[#8A0408] text-white px-8 py-4 rounded-lg font-bold text-lg transition-colors shadow-lg" data-id="element-4858">
              <Phone className="mr-3" size={24} data-id="element-4859" />
              Call (248) 478-8382
            </a>
            <p className="mt-4 text-gray-300" data-id="element-4860">
              24175 Industrial Park Dr., Farmington Hills, MI 48335
            </p>
          </div>
        </div>
      </div>
    </div>;
}